import { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isMobile } from 'react-device-detect';
import { TablePage } from '../../../../containers';
import api from '../../../../services/api';
import toastOptions from '../../../../utils/toastOptions';
import { utils } from '../../../../utils/utils';
import { currencyMask, dateMask, displayDate, handleFocus } from '../../../../utils/masks';
import moment from '../../../../utils/momentConfig';
import CongregationSelect from '../../../../components/congregation-select';
import { TransactionType } from '../../../../models/TransactionType';
import { EXPENSE_CATEGORIES, FinancialCategory, INCOME_CATEGORIES } from '../../../../models/enums/FinancialCategory';
import { faCheck, faCircleCheck, faCircleInfo, faCircleMinus, faCirclePlus, faEllipsisVertical, faFileExport, faFilter, faGear, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Congregation from '../../../../models/Congregation';

interface MenuItem {
  show?: boolean;
  item: React.ReactNode;
}

interface Account {
  id: number;
  description?: string;
  name?: string;
}

interface Transaction {
  id: number;
  description: string;
  amount: number;
  value?: number;
  installmentNumber?: number;
  noOfTotalInstallments?: number;
  type: TransactionType;
  category?: FinancialCategory;
  dueDate: Date;
  paidAt?: Date;
  occurredAt?: Date;
  accountId?: number;
  idAccount?: number;
  idCongregation?: number;
  congregation?: Congregation;
}

export default function TransactionList() {  
  const hasInitializedDateFilter = useRef(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [filterType, setFilterType] = useState<TransactionType | ''>('');
  const [filterCategory, setFilterCategory] = useState<FinancialCategory | ''>('');
  const [filterName, setFilterName] = useState<string>('');
  const previousFilterName = useRef<string>('');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [filterCongregation, setFilterCongregation] = useState<number>(0);
  const [filterStartDate, setFilterStartDate] = useState<string>(() => moment().format('DD/MM/YYYY'));
  const [filterEndDate, setFilterEndDate] = useState<string>(() => moment().format('DD/MM/YYYY'));
  const [refresh, setRefresh] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [effectiveStatus, setEffectiveStatus] = useState<Map<number, boolean>>(new Map());
  const [summary, setSummary] = useState({
    totalPending: 0,
    totalIncomeReceived: 0,
    totalExpense: 0,
    balance: 0
  });

  const actionsItems = (row: Transaction, isSettingEffective: boolean) => ([
    {
      show: !row.paidAt,
      item: <a
        href="#"
        className="uk-margin-small-right uk-text-primary"
        uk-tooltip="title: Efetivar transação"
        onClick={(e) => {
          e.preventDefault();
          handleSetEffectiveTransaction(row);
        }}
      >
        {isSettingEffective ? (
          <div uk-spinner="ratio: 0.5"></div>
        ) : (
          <FontAwesomeIcon icon={faCheck} size={isMobile ? 'lg' : '1x'} />
        )}
      </a>
    },
    {
      show: true,
      item: <Link
        to={`/financeiro/transacoes/${row.id}`}
        uk-tooltip="title: Editar"
        className="uk-margin-small-right uk-text-primary"
        target="_blank"
      >
        <FontAwesomeIcon icon={faPencil} size={isMobile ? 'lg' : '1x'} />
      </Link>
    }
  ] as MenuItem[]);

  const columns = [
    {
      name: 'Data',
      sortable: true,
      cell: (row: Transaction) => (<div>
        <span
          className={`uk-margin-xsmall-right ${!!row.paidAt ? 'uk-text-success' : 'uk-text-primary'}`}
          uk-tooltip={`title: ${!!row.paidAt ? 'Pago' : 'Pendente'}`}
        >
          <FontAwesomeIcon icon={!!row.paidAt ? faCircleCheck : faCircleInfo} />
        </span>
        <span className="uk-text-xsmall uk-text-muted uk-text-nowrap">
          {displayDate(row.dueDate?.toString())}
        </span>
      </div>)
    },
    {
      name: 'Descrição',
      sortable: true,
      grow: 2,
      cell: (row: Transaction) => (
        <div className="uk-text-xsmall uk-flex uk-flex-column uk-flex-nowrap uk-margin-xsmall-y">
          <div>
            <span
              className={`uk-text-xsmall uk-margin-xsmall-right uk-text-meta ${row.type === TransactionType.INCOME ? 'uk-text-success' : 'uk-text-danger'}`}
            >
              {row.type === TransactionType.INCOME ? 'Receita' : 'Despesa'}
            </span>
            {row.idCongregation && <span className="uk-text-muted uk-text-xsmall uk-text-nowrap uk-text-meta">({row.congregation?.description})</span>}
          </div>
          {!!row.installmentNumber && !!row.noOfTotalInstallments && (
            <span className="uk-text-muted uk-text-xsmall uk-text-nowrap uk-text-meta">
              ({row.installmentNumber}/{row.noOfTotalInstallments})
            </span>
          )}
          {row.description && (<span className="uk-text-muted uk-text-xsmall uk-text-italic uk-text-meta">{row.description}</span>)}
        </div>
      )
    },
    {
      name: 'Categoria',
      sortable: true,
      cell: (row: Transaction) => (
        <span className="uk-text-muted uk-text-small uk-text-nowrap uk-text-meta">{row.category}</span>
      )
    },
    {
      name: 'Valor',
      sortable: true,
      cell: (row: Transaction) => (
        <span className={`uk-text-small uk-text-bold ${row.type === TransactionType.INCOME ? 'uk-text-success' : 'uk-text-danger'}`}>
          {currencyMask(row.amount || 0)}
        </span>
      )
    },
    {
      name: 'Ações',
      sortable: false,
      cell: (row: Transaction) => {
        const isSettingEffective = effectiveStatus.get(row.id) === true;
        const items = actionsItems(row, isSettingEffective);

        return isMobile ? (
          <div className="uk-inline">
            <a href="#" onClick={(ev) => { ev.preventDefault(); }}>
              <FontAwesomeIcon icon={faEllipsisVertical} size="lg" />
            </a>
            <div uk-dropdown="mode: click; pos: center; boundary: ! .uk-button-group;">
              <ul className="uk-nav uk-dropdown-nav">
                {items.map((item, index) => (
                  item?.show && (
                    <li key={`action-item-${row.id}-${index}`} className="uk-flex uk-flex-center">
                      {item.item}
                    </li>
                  )
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="uk-flex uk-flex-middle">
            {items.map((item, index) => (
              item?.show && <div key={`action-inline-${row.id}-${index}`}>{item.item}</div>
            ))}
          </div>
        );
      }
    }
  ];

  const handleSetEffectiveTransaction = async (row: Transaction) => {
    if (row.paidAt) {
      return;
    }

    setEffectiveStatus((prev) => {
      const next = new Map(prev);
      next.set(row.id, true);
      return next;
    });

    try {
      await api.put(`/transactions/${row.id}/paid`, {
        paidAt: new Date()
      });
      toast.success('Transação efetivada com sucesso!', toastOptions);
      handleFilterChange();
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setEffectiveStatus((prev) => {
        const next = new Map(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const buildFilterParams = () => {
    const params: any = {};
    if (filterType) {
      params.type = filterType;
    }
    if (filterCategory) {
      params.category = filterCategory;
    }
    if (filterStartDate) {
      params.startDate = moment(filterStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
    }
    if (filterEndDate) {
      params.endDate = moment(filterEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
    }
    if (filterName) {
      params.name = filterName.toUpperCase();
    }
    if (filterCongregation > 0) {
      params.congregationId = filterCongregation;
    }
    return params;
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { data } = await api.get('/transactions/report', {
        params: buildFilterParams()
      });
      const name = `transacoes-${new Date().getTime()}.pdf`;
      utils.printPDF(name, data.item);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIsExporting(false);
    }
  };

  const fetchTransactions = async (page: number, limit: number) => {
    try {
      const params: any = {
        page: 0,
        limit: 0,
        ...buildFilterParams()
      };

      const response = await api.get('/transactions', {
        params
      });

      const transactions = response.data.data ?? [];
      const totalPending = transactions
        .filter((transaction: Transaction) => !transaction.paidAt && transaction.paidAt == null)
        .reduce((acc: number, transaction: Transaction) => acc + Number(transaction.amount || 0), 0);
      const totalIncomeReceived = transactions
        .filter((transaction: Transaction) => transaction.type === TransactionType.INCOME && !!transaction.paidAt)
        .reduce((acc: number, transaction: Transaction) => acc + Number(transaction.amount || 0), 0);
      const totalExpense = transactions
        .filter((transaction: Transaction) => transaction.type === TransactionType.EXPENSE && !!transaction.paidAt)
        .reduce((acc: number, transaction: Transaction) => acc + Number(transaction.amount || 0), 0);

      setSummary({
        totalPending,
        totalIncomeReceived,
        totalExpense,
        balance: totalIncomeReceived - totalExpense
      });

      setPageNo(page);
      setTotal(response.data.total);

      return response;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });

      setSummary({
        totalPending: 0,
        totalIncomeReceived: 0,
        totalExpense: 0,
        balance: 0
      });
      setTotal(0);
      return {
        data: { data: [], total: 0 }
      };
    }
  };

  const handleFilterChange = () => {
    setPageNo(1);
    setRefresh((current) => !current);
  };

  const shiftDay = (days: number) => {
    setFilterStartDate((prev) => moment(prev, 'DD/MM/YYYY').add(days, 'days').format('DD/MM/YYYY'));
    setFilterEndDate((prev) => moment(prev, 'DD/MM/YYYY').add(days, 'days').format('DD/MM/YYYY'));
    setPageNo(1);
    setRefresh((current) => !current);
  };

  const handleGoToToday = () => {
    const today = moment().format('DD/MM/YYYY');
    setFilterStartDate(today);
    setFilterEndDate(today);
    setPageNo(1);
    setRefresh((current) => !current);
  };

  const currentPeriodLabel = filterStartDate === filterEndDate ? filterStartDate : `${filterStartDate} - ${filterEndDate}`;

  const today = moment().format('DD/MM/YYYY');
  const isCurrentPeriodToday = filterStartDate === today && filterEndDate === today;

  useEffect(() => {
    if (previousFilterName.current === filterName) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      previousFilterName.current = filterName;
      setPageNo(1);
      setRefresh((current) => !current);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [filterName]);

  useEffect(() => {
    if (!hasInitializedDateFilter.current) {
      hasInitializedDateFilter.current = true;
      return;
    }

    const debounceTimer = setTimeout(() => {
      setPageNo(1);
      setRefresh((current) => !current);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [filterStartDate, filterEndDate]);

  const availableFilterCategories = useMemo(() => {
    if (filterType === TransactionType.INCOME) {
      return INCOME_CATEGORIES;
    }

    if (filterType === TransactionType.EXPENSE) {
      return EXPENSE_CATEGORIES;
    }

    return Object.values(FinancialCategory);
  }, [filterType]);

  return (
    <div id="page-transaction-list" className="uk-margin-top">
      <TablePage
        title="Transações"
        subTitle="Registros de movimentações financeiras"
        messageEmpty={<span>Nenhuma <b>transação</b> encontrada.</span>}
        fetch={fetchTransactions}
        columns={columns}
        pageNo={pageNo}
        refresh={refresh}
        showSubHeader
        totalRecords={total}
        showAddButton={false}
        pagination={false}
        extraHeaderButtons={<>
          <div className="uk-flex uk-margin-small-left">
            <button className="uk-button uk-button-small uk-button-primary uk-margin-small-right uk-flex uk-flex-middle"
              type="button"
              disabled={isExporting}
              onClick={handleExport}>
              {`Exportar `}
              {isExporting
                ? <div className="uk-margin-small-left" uk-spinner="ratio: 0.5"></div>
                : <FontAwesomeIcon className="uk-margin-small-left" icon={faFileExport} />}
            </button>
            <button className="uk-button uk-button-small uk-button-success uk-margin-small-right"
              onClick={() => window.location.href = `/financeiro/transacoes/novo?type=${TransactionType.INCOME}`}>
              <div className="uk-inline-clip uk-transition-toggle">
                <div className="uk-transition-scale-up uk-transition-opaque">
                  {`Receita `} <FontAwesomeIcon icon={faCirclePlus} />
                </div>
              </div>
            </button>
            <button className="uk-button uk-button-small uk-button-danger"
              onClick={() => window.location.href = `/financeiro/transacoes/novo?type=${TransactionType.EXPENSE}`}>
              <div className="uk-inline-clip uk-transition-toggle">
                <div className="uk-transition-scale-up uk-transition-opaque">
                  {`Despesa `} <FontAwesomeIcon icon={faCircleMinus} />
                </div>
              </div>
            </button>
            <Link to="/financeiro" className="uk-button uk-button-small uk-button-default uk-margin-small-left">
              Voltar
            </Link>
          </div>
        </>}
        options={
          <div className="uk-margin-top">
            <div className="uk-flex uk-flex-center uk-margin-small-bottom">
              <div className="uk-flex uk-flex-middle uk-flex-nowrap" style={{ gap: '4px' }}>
                <button
                  type="button"
                  className="uk-button uk-button-default uk-button-small"
                  onClick={() => shiftDay(-1)}
                  uk-tooltip="title: Dia anterior"
                >
                  <span uk-icon="icon: chevron-left; ratio: 0.8"></span>
                </button>
                <button
                  type="button"
                  className="uk-button uk-button-default uk-button-small"
                  disabled
                >
                  {currentPeriodLabel}
                </button>
                <button
                  type="button"
                  className="uk-button uk-button-default uk-button-small"
                  onClick={() => shiftDay(1)}
                  uk-tooltip="title: Próximo dia"
                >
                  <span uk-icon="icon: chevron-right; ratio: 0.8"></span>
                </button>
                {!isCurrentPeriodToday && (
                  <button
                    type="button"
                    className="uk-button uk-button-primary uk-button-small"
                    onClick={handleGoToToday}
                    uk-tooltip="title: Voltar para hoje"
                  >
                    Hoje
                  </button>
                )}
              </div>
            </div>

            {/* Filters Accordion */}
            <ul uk-accordion="true">
              <li className="uk-close">
                <a className="uk-accordion-title" href="#"><FontAwesomeIcon icon={faFilter} /> Filtros</a>
                <div className="uk-accordion-content">
                  <div className="uk-grid-small uk-margin-top" uk-grid="true">
                    <div className="uk-search uk-search-default uk-width-1-5@m">
                    <div className="uk-flex uk-flex-middle">
                      <label className="uk-form-label uk-margin-small-right" htmlFor="filterType">Tipo</label>
                      <div className="uk-form-controls uk-width-expand">
                        <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                          <select
                            className="uk-select uk-form-small"
                            id="filterType"
                            value={filterType}
                            onChange={(e) => {
                              const nextType = e.target.value as TransactionType;
                              setFilterType(nextType);
                              if (filterCategory) {
                                const nextCategories = nextType === TransactionType.INCOME
                                  ? INCOME_CATEGORIES
                                  : nextType === TransactionType.EXPENSE
                                  ? EXPENSE_CATEGORIES
                                  : Object.values(FinancialCategory);
                                if (!nextCategories.includes(filterCategory)) {
                                  setFilterCategory('');
                                }
                              }
                              handleFilterChange();
                            }}
                          >
                            <option value="">Todos</option>
                            {Object.values(TransactionType).map((value) => (
                              <option key={value} value={value}>{value}</option>
                            ))}
                          </select>
                          <button
                            className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                            style={{ textTransform: 'none' }}
                            type="button"
                            tabIndex={-1}
                          >
                            <span>{filterType as TransactionType}</span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    </div>

                    <div className="uk-search uk-search-default uk-width-1-5@m">
                      <div className="uk-flex uk-flex-middle">
                        <label className="uk-form-label uk-margin-small-right" htmlFor="filterCategory">Categoria</label>
                        <div className="uk-form-controls uk-width-expand">
                          <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                            <select
                              className="uk-select uk-form-small"
                              id="filterCategory"
                              value={filterCategory}
                              onChange={(e) => {
                                setFilterCategory(e.target.value as FinancialCategory);
                                handleFilterChange();
                              }}
                            >
                              <option value="">Todas</option>
                              {availableFilterCategories.map((value) => (
                                <option key={value} value={value}>{value}</option>
                              ))}
                            </select>
                            <button
                              className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                              style={{ textTransform: 'none' }}
                              type="button"
                              tabIndex={-1}
                            >
                              <span>{filterCategory as FinancialCategory}</span>
                              <span uk-icon="icon: chevron-down"></span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="uk-width-1-6@m">
                      <div className="uk-flex uk-flex-middle">
                        <label className="uk-form-label uk-margin-small-right" htmlFor="filterStartDate">
                          De
                        </label>
                        <input
                          className="uk-input uk-form-small"
                          type="text"
                          id="filterStartDate"
                          placeholder="DD/MM/YYYY"
                          value={filterStartDate}
                          onChange={(e) => {
                            e.target.value = dateMask(e.target.value);
                            setFilterStartDate(e.target.value);
                          }}
                          onFocus={handleFocus}
                        />
                      </div>
                    </div>

                    <div className="uk-width-1-6@m">
                      <div className="uk-flex uk-flex-middle">
                        <label className="uk-form-label uk-margin-small-right" htmlFor="filterEndDate">
                          Até
                        </label>
                        <input
                          className="uk-input uk-form-small"
                          type="text"
                          id="filterEndDate"
                          placeholder="DD/MM/YYYY"
                          value={filterEndDate}
                          onChange={(e) => {
                            e.target.value = dateMask(e.target.value);
                            setFilterEndDate(e.target.value);
                          }}
                          onFocus={handleFocus}
                        />
                      </div>
                    </div>

                    <div className="uk-width-1-4@m">
                    <CongregationSelect
                      afterSelect={(id: number) => {
                        setFilterCongregation(id);
                        handleFilterChange();
                      }}
                    />
                    </div>

                    <div className="uk-search uk-search-default uk-width-1@m">
                      <div className="uk-flex uk-flex-middle">
                        <label className="uk-form-label uk-margin-small-right" htmlFor="filterName">
                          Nome
                        </label>
                        <span className="uk-search-icon-flip" uk-search-icon="true"></span>
                        <input
                          className="uk-search-input uk-input uk-form-small"
                          type="search"
                          placeholder="Pesquise por nome, descrição, categoria..."
                          value={filterName}
                          onChange={(e) => {
                            setFilterName(e.target.value);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
            {/* End Filters Accordion */}

            <div className="uk-grid-small uk-margin-small-top uk-margin-medium-bottom" uk-grid="true">
              <div className="uk-width-1-4@m">
                <div className="uk-card uk-card-default uk-card-small uk-card-body uk-text-center">
                  <div className="uk-text-meta">Pendentes</div>
                  <div className="uk-text-large uk-text-bold">{currencyMask(summary.totalPending)}</div>
                </div>
              </div>
              <div className="uk-width-1-4@m">
                <div className="uk-card uk-card-default uk-card-small uk-card-body uk-text-center">
                  <div className="uk-text-meta">Receitas Recebidas</div>
                  <div className="uk-text-large uk-text-bold uk-text-success">{currencyMask(summary.totalIncomeReceived)}</div>
                </div>
              </div>
              <div className="uk-width-1-4@m">
                <div className="uk-card uk-card-default uk-card-small uk-card-body uk-text-center">
                  <div className="uk-text-meta">Despesas Pagas</div>
                  <div className="uk-text-large uk-text-bold uk-text-danger">{currencyMask(summary.totalExpense)}</div>
                </div>
              </div>
              <div className="uk-width-1-4@m">
                <div className="uk-card uk-card-default uk-card-small uk-card-body uk-text-center">
                  <div className="uk-text-meta">Saldo</div>
                  <div className={`uk-text-large uk-text-bold ${summary.balance >= 0 ? 'uk-text-success' : 'uk-text-danger'}`}>
                    {currencyMask(summary.balance)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      />      
    </div>
  );
}
